import { type FieldFunctionOptions } from "@apollo/client"
import { type KeyArgsFunction } from "@apollo/client/cache/inmemory/policies"
import { type Pagination } from "@digits-graphql/frontend/graphql-bearer"
import { type ExistsFn } from "src/shared/initializers/typePolicies/helpers/mergeReducers"
import { allKeyArgs } from "./allKeyArgs"

// Do not key off pagination fields.
// We want to cache responses by all the filters except these in order to support infinite scroll
const EXCLUDED_KEYS = ["pagination", "page"]

interface PaginationKey {
  pagination?: Pagination | null
}

interface PageKey {
  page?: Pagination | null
}

type PaginationVars = PaginationKey | PageKey

const paginationKeyArgs = (args: PaginationVars) => allKeyArgs(args, EXCLUDED_KEYS)

export function offsetLimitPagination<
  TVars extends PaginationVars = PaginationVars,
  TData = object,
>(exists?: ExistsFn<TData>, keyArgs?: KeyArgsFunction) {
  return {
    keyArgs: keyArgs ?? paginationKeyArgs,
    merge(existing: TData[], incoming: TData[], options: FieldFunctionOptions<TVars>) {
      const { args } = options
      // not an array, unsupported (you have to do your own merge function)
      if (!existing?.slice) {
        return incoming
      }

      const start = paginationOffset(args)

      // An offset-0 response is the only known-good prefix for a potentially changed list.
      // Drop old cached tail entries instead of preserving stale, duplicate-prone rows.
      if (start === 0) {
        return incoming
      }

      // new copy of the existing values
      const merged = existing.slice(0)

      for (const [index, newElem] of incoming.entries()) {
        const existsInList = exists ? exists(merged, newElem) : false

        if (!existsInList) {
          merged[start + index] = newElem
        }
      }

      return merged
    },
  }
}

const paginationOffset = (args: PaginationVars | null | undefined) => {
  if (!args) return 0

  const offsetFromPagination = "pagination" in args ? args.pagination?.offset : undefined
  const offsetFromPage = "page" in args ? args.page?.offset : undefined

  return offsetFromPagination ?? offsetFromPage ?? 0
}
